// Field registry for the advanced filter builder — Polish B2.
//
// Single source of truth for the combobox options. Every field id
// maps to a backend allow-list entry and a value-type that picks
// the right value picker.

import type { UiOperator } from "./operators";


export type FieldValueType =
  | "user"            // multi-select user picker (memberships endpoint)
  | "status"          // multi-select status enum
  | "priority"        // single-select priority enum
  | "account"         // multi-select account picker
  | "tag"             // multi-select tag picker
  | "complexity"      // single-select complexity enum
  | "boolean"         // single yes/no
  | "date"            // date picker
  | "text";           // free text


export interface FieldDescriptor {
  /** Stable id; matches the wire ``field`` key. */
  id: string;
  /** Human label shown in the field combobox. */
  label: string;
  valueType: FieldValueType;
  /** UI operators surfaced for this field. The codec maps each one
   * onto the correct wire operator at serialise time. */
  operators: UiOperator[];
}


export const FIELD_REGISTRY: FieldDescriptor[] = [
  {
    id: "due_date_time",
    label: "Due date",
    valueType: "date",
    operators: [
      "is_on",
      "is_before",
      "is_before_or_on",
      "is_after",
      "is_after_or_on",
      "is_empty",
      "is_not_empty",
    ],
  },
  {
    id: "status",
    label: "Status",
    valueType: "status",
    operators: ["is", "is_not"],
  },
  {
    id: "assignee_id",
    label: "Assignee",
    valueType: "user",
    operators: ["is", "is_not", "is_empty", "is_not_empty"],
  },
  {
    id: "created_by_id",
    label: "Created by",
    valueType: "user",
    operators: ["is", "is_not"],
  },
  {
    id: "client_id",
    label: "Account",
    valueType: "account",
    operators: ["is", "is_not"],
  },
  {
    id: "tag_ids",
    label: "Tags",
    valueType: "tag",
    operators: ["is", "is_not"],
  },
  {
    id: "priority",
    label: "Priority",
    valueType: "priority",
    operators: ["is", "is_not"],
  },
  {
    id: "is_regulatory",
    label: "Regulatory",
    valueType: "boolean",
    operators: ["is"],
  },
  {
    id: "complexity",
    label: "Complexity",
    valueType: "complexity",
    operators: ["is", "is_not"],
  },
];


export function getField(id: string): FieldDescriptor | undefined {
  return FIELD_REGISTRY.find((f) => f.id === id);
}


export function isMultiValue(valueType: FieldValueType): boolean {
  // ``user`` / ``status`` / ``account`` / ``tag`` are multi; everything
  // else is single. The codec uses this to decide whether ``is`` →
  // ``in`` or ``eq``.
  return ["user", "status", "account", "tag"].includes(valueType);
}
